import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useSWRConfig } from "swr";
import { api } from "services/api.service";

export type LocationUploadError = {
  row: number;
  code: string;
  messages: string[];
};

export type LocationUploadResult = {
  created: { id: number; code: string }[];
  errors: LocationUploadError[];
};

type UploadLocationsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const addedLabel = (count: number) => `Added ${count} ${count === 1 ? "location" : "locations"}.`;

const messageFrom = (error: unknown) => {
  const errors = (error as { response?: { data?: { errors?: unknown } } }).response?.data?.errors;
  if (Array.isArray(errors) && errors.every((item) => typeof item === "string")) return errors.join(" ");
  return "The file could not be uploaded.";
};

export const UploadLocationsDialog = ({ open, onClose }: UploadLocationsDialogProps) => {
  const { mutate } = useSWRConfig();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [result, setResult] = useState<LocationUploadResult | null>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setUploading(false);
    setFileError(null);
    setResult(null);
  }, [open]);

  const upload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    setFileError(null);
    setResult(null);
    try {
      const response = await api.post<LocationUploadResult>("/location_management/locations/bulk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(response.data);
      if (response.data.created.length > 0) {
        mutate((key) => typeof key === "string" && key.startsWith("/location_management/locations"), undefined, {
          revalidate: true,
        });
      }
    } catch (error) {
      setFileError(messageFrom(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Upload locations</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            Each row needs a code. Type, kind, parent, and description can be left blank. Type and kind are the names of
            existing records. Parent is a location code, or the code of another row in this file.
          </DialogContentText>
          <Link href="/location_upload_template.csv" download>
            Download template
          </Link>
          <Button variant="outlined" component="label" sx={{ alignSelf: "flex-start" }}>
            {file ? file.name : "Choose CSV"}
            <input
              hidden
              type="file"
              accept=".csv,text/csv"
              aria-label="CSV file"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setResult(null);
                setFileError(null);
              }}
            />
          </Button>
          {fileError && <Alert severity="error">{fileError}</Alert>}
          {result && (
            <>
              <Alert severity={result.errors.length > 0 ? "warning" : "success"}>
                {addedLabel(result.created.length)}
              </Alert>
              {result.errors.length > 0 && (
                <Table size="small" aria-label="Location upload errors">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((error) => (
                      <TableRow key={error.row}>
                        <TableCell>{error.row}</TableCell>
                        <TableCell>{error.code}</TableCell>
                        <TableCell>{error.messages.join(" ")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={upload} disabled={!file || uploading}>
          {uploading ? "Uploading" : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
